import { SoundKey, SoundDef, SOUND_KEYS } from "./manifest";
import { getSounds } from "../api/sounds";

const AMBIENT_KEY: SoundKey = "ambient";

// Runtime manifest fetched from GET /api/sounds/ (admin-managed). Replaces the
// former static MANIFEST constant.
let manifest = new Map<SoundKey, SoundDef>();

// Module-level singleton. The audio engine is one global resource per tab —
// React owns triggers but not the engine itself. Tests reset via
// __resetForTesting() and inject a fake context via __setAudioContextFactoryForTesting().

const MUTE_STORAGE_KEY = "audio:muted";

type AudioContextFactory = () => AudioContext;

let audioContextFactory: AudioContextFactory = () => {
  // window.webkitAudioContext for Safari < 14.
  const Ctor =
    window.AudioContext ||
    (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
  return new Ctor();
};

let audioContext: AudioContext | null = null;
let masterGain: GainNode | null = null;
const bufferMap = new Map<SoundKey, AudioBuffer>();
const listeners = new Set<() => void>();
let loadPromise: Promise<void> | null = null;
let muted = readMutedFromStorage();
// `started` = the audio context has been unlocked by a user gesture, so sound
// is actually audible. Browsers block audio until the first gesture, so before
// it nothing plays — the UI reflects this (the toggle reads "off" until then).
let started = false;
let ambientStarted = false;
let gestureCleanup: (() => void) | null = null;

function readMutedFromStorage(): boolean {
  try {
    return window.localStorage.getItem(MUTE_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

function writeMutedToStorage(value: boolean): void {
  try {
    window.localStorage.setItem(MUTE_STORAGE_KEY, value ? "true" : "false");
  } catch {
    /* private mode / disabled — ignore */
  }
}

function emit(): void {
  listeners.forEach((l) => l());
}

function ensureContext(): { ctx: AudioContext; gain: GainNode } {
  if (audioContext && masterGain) return { ctx: audioContext, gain: masterGain };
  audioContext = audioContextFactory();
  masterGain = audioContext.createGain();
  masterGain.gain.value = muted ? 0 : 1;
  masterGain.connect(audioContext.destination);
  return { ctx: audioContext, gain: masterGain };
}

function startAmbient(): void {
  if (ambientStarted) return;
  const buffer = bufferMap.get(AMBIENT_KEY);
  if (!buffer) return;
  ambientStarted = true;
  const { ctx, gain } = ensureContext();
  const source = ctx.createBufferSource();
  source.buffer = buffer;
  source.loop = true;
  const perSoundGain = ctx.createGain();
  perSoundGain.gain.value = manifest.get(AMBIENT_KEY)?.volume ?? 1;
  source.connect(perSoundGain);
  perSoundGain.connect(gain);
  source.start(0);
}

function markStarted(): void {
  if (started) return;
  started = true;
  emit();
}

/**
 * Unlock audio: resume the context, mark it started, and kick off the ambient
 * loop. Idempotent. Called by the first user gesture (armed below) and directly
 * by the sound toggle when a visitor clicks it before any gesture.
 *
 * The context only counts as unlocked once it actually reports "running":
 * resume() is asynchronous and a browser may reject it (the event wasn't an
 * activating one). Until then we stay armed so the next gesture retries, and
 * the toggle keeps reading "off" instead of claiming silent audio is on.
 */
export function startAudio(): void {
  if (started) return;
  let ctx: AudioContext;
  try {
    ctx = ensureContext().ctx;
  } catch {
    return; // no Web Audio in this environment (old browser, jsdom)
  }
  const unlock = () => {
    if (ctx.state !== "running") return;
    markStarted();
    startAmbient();
    if (gestureCleanup) gestureCleanup();
  };
  if (ctx.state === "running") {
    unlock();
    return;
  }
  void Promise.resolve(ctx.resume()).then(unlock, () => {
    /* browser refused this gesture — stay armed for the next one */
  });
}

// Every event the HTML spec treats as user activation. pointerdown alone misses
// touch (iOS activates on touchend) and misses clicks synthesised without a
// preceding pointer event.
const GESTURE_EVENTS = ["pointerdown", "pointerup", "touchend", "click", "keydown"] as const;

// Arm listeners so the first user gesture anywhere on the page unlocks audio
// (browser autoplay policy — it cannot be bypassed). Armed as soon as loading
// starts, *before* buffers arrive: the ambient track is hundreds of kilobytes
// and a visitor who clicks during that window would otherwise have their
// gesture dropped, leaving the site silent until they happened to click again.
function armFirstGesture(): void {
  if (started) return;
  // Already unlocked (a permissive autoplay policy) — nothing to wait for.
  // Checked without creating a context: arming must not force one into
  // existence before we even know there are sounds to play.
  if (audioContext?.state === "running") {
    startAudio();
    return;
  }
  if (gestureCleanup) return;
  const onGesture = () => startAudio();
  GESTURE_EVENTS.forEach((type) => window.addEventListener(type, onGesture));
  gestureCleanup = () => {
    GESTURE_EVENTS.forEach((type) => window.removeEventListener(type, onGesture));
    gestureCleanup = null;
  };
}

export function ensureLoaded(): Promise<void> {
  if (loadPromise) return loadPromise;
  loadPromise = (async () => {
    armFirstGesture();
    try {
      const entries = await getSounds();
      manifest = new Map(
        entries
          .filter((e) => SOUND_KEYS.includes(e.key))
          .map((e) => [e.key, { src: e.src, volume: e.volume }]),
      );
    } catch {
      // Manifest unavailable (network/API error): stay silent, no console noise.
      emit();
      return;
    }
    await Promise.all(
      Array.from(manifest.entries()).map(async ([key, def]) => {
        let res: Response;
        try {
          res = await fetch(def.src);
        } catch {
          return; // network error: skip silently, treat as absent
        }
        if (!res.ok) return; // 404 → absent, no console noise
        const arrayBuf = await res.arrayBuffer();
        const { ctx } = ensureContext();
        try {
          const audioBuf = await ctx.decodeAudioData(arrayBuf);
          bufferMap.set(key, audioBuf);
        } catch {
          /* malformed audio: treat as absent */
        }
      }),
    );
    // A gesture may have unlocked audio while the buffers were still arriving —
    // the ambient loop had nothing to play back then, so start it now.
    if (started) startAmbient();
    armFirstGesture();
    emit();
  })();
  return loadPromise;
}

export function play(key: SoundKey, opts?: { detune?: number }): void {
  if (muted) return;
  const buffer = bufferMap.get(key);
  if (!buffer) return;
  const { ctx, gain } = ensureContext();
  if (ctx.state === "suspended") {
    // Fire-and-forget: by the time the browser has a user gesture (which it
    // does — the reading flow requires several clicks before any sound), this
    // resolves synchronously enough for the first start(0).
    void ctx.resume();
  }
  markStarted();
  // An effect can be the first sound of the session (its click unlocked audio
  // before the window listener ran) — the background loop must come along.
  startAmbient();
  const source = ctx.createBufferSource();
  source.buffer = buffer;
  source.detune.value = opts?.detune ?? 0;
  const perSoundGain = ctx.createGain();
  perSoundGain.gain.value = manifest.get(key)?.volume ?? 1;
  source.connect(perSoundGain);
  perSoundGain.connect(gain);
  source.start(0);
}

export function setMuted(value: boolean): void {
  muted = value;
  writeMutedToStorage(value);
  if (masterGain) masterGain.gain.value = value ? 0 : 1;
  emit();
}

export interface Snapshot {
  isMuted: boolean;
  hasAnySound: boolean;
  /** Audio has been unlocked by a user gesture (sound is actually playing). */
  started: boolean;
}

let cachedSnapshot: Snapshot = { isMuted: muted, hasAnySound: false, started: false };

export function getSnapshot(): Snapshot {
  // useSyncExternalStore requires referentially stable snapshots between
  // emits — otherwise React loops. Rebuild only when state actually changes.
  const next: Snapshot = { isMuted: muted, hasAnySound: bufferMap.size > 0, started };
  if (
    next.isMuted !== cachedSnapshot.isMuted ||
    next.hasAnySound !== cachedSnapshot.hasAnySound ||
    next.started !== cachedSnapshot.started
  ) {
    cachedSnapshot = next;
  }
  return cachedSnapshot;
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

// --- Test-only injection points ---
export function __setAudioContextFactoryForTesting(fn: AudioContextFactory): void {
  audioContextFactory = fn;
}

export function __resetForTesting(): void {
  audioContext = null;
  masterGain = null;
  bufferMap.clear();
  if (gestureCleanup) gestureCleanup();
  started = false;
  ambientStarted = false;
  manifest = new Map();
  listeners.clear();
  loadPromise = null;
  muted = false;
  cachedSnapshot = { isMuted: false, hasAnySound: false, started: false };
}
